<template>
  <main class="section products-page" aria-labelledby="all-products-title">
    <div class="container">
      <h1 id="all-products-title" class="title">{{ t('products.title') }}</h1>
      <p class="subtitle">{{ t('products.subtitle') }}</p>

      <div class="grid">
        <RouterLink v-for="(p, i) in products" :key="p.id" :to="'/products/' + p.id" class="card" :style="styleFor(i)">
          <div class="thumb" aria-hidden="true">
            <img class="img" :src="p.image" :alt="pTitle(p)" loading="lazy" />
          </div>
          <h2 class="card-title">{{ pTitle(p) }}</h2>
          <p class="card-desc">{{ pDesc(p) }}</p>
          <div class="actions">
            <span class="details-btn">{{ t('details.viewDetails') }}</span>
          </div>
        </RouterLink>
      </div>
    </div>
  </main>
</template>

<script setup>
import { useI18n } from 'vue-i18n'
import { products } from '../data/products'

const { t, locale } = useI18n()
const hues = [200, 215, 225, 210, 230]
const styleFor = (i) => ({ '--h': hues[i % hues.length] })
const pTitle = (p) => p[locale.value === 'zh-CN' ? 'zh' : locale.value]?.title || p.en.title
const pDesc = (p) => p[locale.value === 'zh-CN' ? 'zh' : locale.value]?.desc || p.en.desc
</script>

<style scoped>
.section.products-page {
  padding: 3.5rem 1.5rem 4rem;
  background: radial-gradient(1200px 600px at 10% -20%, rgba(96,165,250,0.08), transparent),
              radial-gradient(900px 500px at 110% 120%, rgba(37,99,235,0.10), transparent),
              linear-gradient(180deg, rgba(8,12,24,1) 0%, rgba(10,14,28,1) 100%);
  transition: background 600ms ease;
}
.container { max-width: 1100px; margin: 0 auto; }
.title { color: #e6f0ff; font-size: clamp(1.6rem, 4vw, 2.2rem); text-align: center; }
.subtitle { color: #9fb0ff; text-align: center; margin-bottom: 2rem; }
.grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1.5rem; align-items: stretch; }

.card {
  display: flex; flex-direction: column;
  padding: 1.5rem; border-radius: 16px; text-decoration: none;
  background: linear-gradient(180deg, rgba(18,22,40,0.9), rgba(14,18,34,0.9));
  border: 1px solid rgba(255,255,255,0.08);
  box-shadow: 0 10px 30px rgba(0,0,0,0.25), inset 0 0 0 1px rgba(255,255,255,0.02);
  transition: transform .2s ease, box-shadow .2s ease;
  position: relative;
  overflow: hidden;
}
.card::before {
  content: '';
  position: absolute; inset: -1px;
  background: conic-gradient(from 0deg, hsla(var(--h),90%,60%,.0), hsla(var(--h),90%,60%,.35), hsla(var(--h),90%,60%,.0));
  filter: blur(16px); opacity: .35; transform: rotate(0deg);
  pointer-events: none;
}
.card:hover { transform: translateY(-4px); box-shadow: 0 16px 40px rgba(0,0,0,0.35); }
.card-title { color: #ebf3ff; margin: 0 0 .4rem; font-size: 1.4rem; }
.card-desc { color: #b7c5ff; margin: 0; font-size: 1.1rem; }

/* Details action */
.actions { display: flex; justify-content: flex-end; margin-top: auto; }
.details-btn {
  display: inline-block; padding: .5rem .85rem; border-radius: 999px; font-size: .95rem;
  color: #e9f2ff; background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.12);
  position: relative; overflow: hidden;
}
.details-btn::after {
  content: ''; position: absolute; inset: 0; pointer-events: none;
  background: radial-gradient(120% 60% at -10% 50%, rgba(96,165,250,.35), transparent 60%),
              radial-gradient(120% 60% at 110% 50%, rgba(37,99,235,.35), transparent 60%);
  opacity: 0; transition: opacity .25s ease;
}
.card:hover .details-btn::after { opacity: .9; }
@media (prefers-reduced-motion: no-preference) {
  .details-btn { transition: transform .2s ease, box-shadow .2s ease, filter .2s ease; box-shadow: 0 6px 18px rgba(0,0,0,.18); }
  .card:hover .details-btn { transform: translateY(-1px); filter: brightness(1.05); box-shadow: 0 10px 26px rgba(0,0,0,.28); }
}
</style>


/* Thumbnail image */
.thumb { position: relative; border-radius: 12px; overflow: hidden; aspect-ratio: 16 / 9; background: rgba(255,255,255,0.04); margin-bottom: .7rem; }
.img { width: 100%; height: 100%; object-fit: cover; display: block; filter: saturate(1.2) contrast(1.05); }
.thumb::after { content: ''; position: absolute; inset: 0; background: linear-gradient(180deg, rgba(0,0,0,0) 30%, rgba(0,0,0,0.15) 100%); }
